import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import InputCheckbox from 'common/components/Elements/Form/InputCheckbox/InputCheckbox';
import Icon from 'common/components/Elements/Icon/Icon';
import ItemsPagination from 'common/components/Elements/ItemsPagination/ItemsPagination';
import { arrayFlat, arrayIntersection,arraySum } from 'common/utilities/core/array';
import { enqueueWarningNotification } from 'core/redux/actions/notification/notificationActions';
import type { AppDispatch } from 'core/redux/store/configureStore.types';
import I18n from 'i18n/en-US';
import React from 'react';
import { useDispatch } from 'react-redux';
import type { SequenceAlign, SequenceType } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import { createSearchActiveMatch } from 'sequenceAnalyser/redux/actions/sequenceAnalyserActions';
import type { SequenceSearchMatch, SequencesSearch, SequencesSearchScope } from 'sequenceAnalyser/utilities/search/search';
import { isValidResidues } from 'sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser';

import classes from './Search.style';

interface ComponentProps {
  onSearchChange: (query: string, scope: SequencesSearchScope) => void;
  search: SequencesSearch;
  hasSelected: boolean;
  sessionSequenceType: SequenceType;
  alignedSequences: SequenceAlign[];
}

const sequenceSearchMatchId = (match: SequenceSearchMatch) => `${match.residueId}-${match.start}-${match.end}`;

function Search({
  search, onSearchChange, hasSelected, sessionSequenceType, alignedSequences,
}: ComponentProps) {
  const ids = arrayIntersection(alignedSequences.map((s) => s.id.toString()), Object.keys(search.matches));
  const matches = arrayFlat<SequenceSearchMatch>(ids.map((id) => search.matches[id]));
  const matchesIds = matches.map((match) => sequenceSearchMatchId(match));
  const defaultMatchId = matchesIds[0];
  const hasMatches = matchesIds.length > 0;
  const [activeMatch, setActiveMatch] = React.useState<string | undefined>(defaultMatchId);
  const activeMatchMatch = matches.find((m) => sequenceSearchMatchId(m) === activeMatch);
  const dispatch = useDispatch<AppDispatch>();

  // When the user change active match
  React.useEffect(() => {
    if (activeMatchMatch) {
      dispatch(createSearchActiveMatch(activeMatchMatch));
    }
  }, [activeMatch, activeMatchMatch, dispatch]);

  // When the search props update (new search)
  React.useEffect(() => {
    setActiveMatch(defaultMatchId);
  }, [defaultMatchId]);

  return (
    <Box display="inline-block">
      <Box sx={classes.search}>
        <InputBase
          sx={classes.input}
          value={search.query}
          placeholder={I18n.SequenceAnalyser.Search.placeholder}
          inputProps={{
            'aria-label': I18n.SequenceAnalyser.Search.placeholder,
          }}
          endAdornment={search.query.length > 0 ? (
            <ClearIcon
              onClick={() => onSearchChange('', 'all')}
              style={{ fontSize: '16px', cursor: 'pointer' }}
            />
          ) : ''}
          onChange={(e) => {
            const newQuery = e.currentTarget.value.replace(/\s/g, '').toUpperCase();

            if (isValidResidues(newQuery, sessionSequenceType)) {
              onSearchChange(newQuery, search.scope);
            } else {
              dispatch(enqueueWarningNotification(I18n.SequenceAnalyser.Search.invalidResidues));
            }
          }}
        />
        <IconButton
          type="submit"
          sx={classes.iconButton}
          aria-label="search"
          size="small"
        >
          <Icon element={SearchIcon} />
        </IconButton>
      </Box>
      {search.query.length > 0 && (
        <Box sx={classes.results}>
          <Box display="flex">
            <Typography component="span">
              {hasMatches
                ? `${arraySum(Object.values(search.matches).map((m) => m.length))} matches`
                : 'No result'}
            </Typography>
            {activeMatch && (
              <Box flexGrow={1} display="inline-block" textAlign="right">
                <ItemsPagination
                  activeItem={activeMatch}
                  items={matchesIds}
                  onItemChange={(item) => {
                    setActiveMatch(item);
                  }}
                />
              </Box>
            )}
          </Box>
          <Box mb={-1}>
            <InputCheckbox
              label={I18n.SequenceAnalyser.Search.selectionOnly}
              checked={search.scope === 'selection'}
              onChange={() => {
                onSearchChange(search.query, search.scope === 'all' ? 'selection' : 'all');
              }}
              disabled={!hasSelected}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
export default Search;
